<template>
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ $t('app.tabs.profile') }}</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="handleEdit" data-cy="edit-button">
            <ion-icon :icon="pencilOutline" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <ion-refresher slot="fixed" @ionRefresh="refreshData($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>



      <template v-if="isLoading">
        <ion-card class="m-md md:max-w-[600px] md:mx-auto md:my-lg bg-white">
          <ion-card-header class="text-center">
            <div class="relative inline-block mb-md">
              <div class="w-[80px] h-[80px] rounded-full border-[3px] border-primary mx-auto overflow-hidden" data-cy="skeleton-avatar">
                <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
              </div>
              <div class="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 w-[90px] h-[22px] rounded-md" data-cy="skeleton-badge">
                <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
              </div>
            </div>
            <div class="w-[45%] h-[24px] mb-1" data-cy="skeleton-display-name">
              <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
            </div>
            <div class="w-[25%] h-[16px]" data-cy="skeleton-username">
              <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
            </div>
          </ion-card-header>

          <ion-card-content>
            <div class="text-center mb-lg">
              <div class="w-[30%] h-[24px] mx-auto mb-1">
                <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
              </div>
              <div class="w-[20%] h-[18px] mx-auto">
                <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
              </div>
            </div>

            <div class="py-md">
              <div class="flex justify-between text-sm text-text-secondary mb-xs">
                <div class="w-[80px] h-[22px] rounded-md">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
                <div class="w-[80px] h-[22px] rounded-md">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
              </div>
              <div class="h-[5px]">
                <ion-skeleton-text class="w-full h-full mt-sm bg-primary/30" :animated="true"></ion-skeleton-text>
              </div>
              <div class="w-[45%] h-[14px] mx-auto mt-2 text-center">
                <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
              </div>
            </div>
          </ion-card-content>
        </ion-card>

        <div class="px-sm py-md md:max-w-[600px] md:mx-auto md:px-lg">
          <ion-list-header class="px-md py-0 mb-1">
            <div class="w-[50%] h-[20px]">
              <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
            </div>
          </ion-list-header>
          <ion-list>
            <ion-item v-for="n in 2" :key="n" class="my-xs mx-0 rounded-sm bg-white" 
                style="--padding-start: 16px; --padding-end: 16px; --padding-top: 12px; --padding-bottom: 12px;">
              <div class="mr-sm" slot="start">
                <ion-skeleton-text class="w-[24px] h-[24px] rounded-full" :animated="true"></ion-skeleton-text>
              </div>
              <ion-label>
                <div class="w-[65%] h-[18px] mb-md">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
                <div class="w-[50%] h-[14px] mb-sm">
                  <ion-skeleton-text class="w-full h-full" :animated="true"></ion-skeleton-text>
                </div>
              </ion-label>
              <div class="w-[70px] h-[24px] rounded-md mr-1" slot="end">
                <ion-skeleton-text class="w-full h-full rounded-md" :animated="true"></ion-skeleton-text>
              </div>
            </ion-item>
          </ion-list>
        </div>
      </template>

      <template v-else>
        <ion-card class="m-md md:max-w-[600px] md:mx-auto md:my-lg bg-white animate-scale-in" data-cy="profile-card">
          <ion-card-header class="text-left">
            <div class="relative inline-block mb-md">
              <ion-avatar class="w-[80px] h-[80px] mx-auto border-[3px] border-primary" data-cy="user-avatar">
                <img :src="userStore.user?.avatar_url" :alt="userStore.user?.display_name">
              </ion-avatar>
              <ion-badge 
                class="absolute -bottom-2 left-1/2 transform -translate-x-1/2 text-xs px-sm py-xs rounded-sm" 
                :color="getMembershipColor()" 
                data-cy="membership-badge"
              >
                {{ getLocalizedTier(userStore.user?.tags?.membership_type || 'regular') }}
              </ion-badge>
            </div>
            <div class="m-sm">
              <ion-card-title data-cy="user-name" class="text-2xl font-bold mb-xs">{{ userStore.user?.display_name }}</ion-card-title>
              <ion-card-subtitle data-cy="user-username">@{{ userStore.user?.username }}</ion-card-subtitle>
            </div>
          </ion-card-header>

          <ion-card-content>
            <div class="text-center mb-lg">
              <div class="text-2xl font-bold text-primary points-display" data-cy="points-display">
                {{ userStore.userPoints }}
              </div>
              <div class="text-sm text-text-secondary mt-xs" data-cy="points-label">
                {{ $t('common.totalPoints') }}
              </div>
            </div>

            <div class="py-md" data-cy="tier-progress">
              <div class="flex justify-between text-sm text-text-secondary mb-xs">
                <ion-badge :color="getTierColor(getCurrentTier(userStore.userPoints || 0))">
                  {{ getLocalizedTier(getCurrentTier(userStore.userPoints || 0)) }}
                </ion-badge>
                <ion-badge :color="getTierColor(getNextTier(userStore.userPoints || 0))">
                  {{ getLocalizedTier(getNextTier(userStore.userPoints || 0)) }}
                </ion-badge>
              </div>
              <ion-progress-bar
                :value="getTierProgress(userStore.userPoints || 0)"
                class="h-xs"
                color="primary"
                style="--background: var(--color-background-secondary);"
              ></ion-progress-bar>
              <div class="text-center text-xs text-text-tertiary mt-xs" data-cy="points-to-next-tier">
                {{ getPointsToNextTier(userStore.userPoints || 0) }} {{ $t('profile.pointsToNextTier') }}
              </div>
            </div>
          </ion-card-content>
        </ion-card>

        <div class="px-md py-md md:max-w-[600px] md:mx-auto md:px-lg">
          <ion-list-header data-cy="rewards-section-header">{{ $t('profile.recentRewards') }}</ion-list-header>
          <ion-list data-cy="rewards-list">
            <ion-item 
              v-for="reward in recentRewards" 
              :key="reward.id" 
              class="my-xs mx-0 rounded-sm bg-white"
              style="--padding-start: var(--space-md); --padding-end: var(--space-md); --padding-top: var(--space-sm); --padding-bottom: var(--space-sm);"
              data-cy="reward-item"
            >
              <ion-icon 
                :icon="reward.icon" 
                slot="start" 
                class="text-xl text-primary mr-sm"
              ></ion-icon>
              <ion-label>
                <h2 class="text-base font-semibold text-text-primary" data-cy="reward-title">{{ reward.name }}</h2>
                <p class="text-sm text-text-secondary" data-cy="reward-date">{{ $t('profile.redeemed') }} {{ formatDate(reward.date) }}</p>
              </ion-label>
              <ion-badge slot="end" color="medium" data-cy="reward-points">{{ reward.points }} {{ $t('leaderboard.pts') }}</ion-badge>
            </ion-item>
          </ion-list>
        </div>
        
        <!-- Whitelabel footer - Subtask 7.3 -->
        <whitelabel-footer />
      </template>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import { ref, onMounted, defineExpose } from 'vue'
import { useUserStore } from '../stores/userStore'
import { useProfileData } from '../composables/useProfileData'
import { useI18n } from 'vue-i18n'
import WhitelabelFooter from '../components/WhitelabelFooter.vue'

import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonCard, IonCardHeader, IonCardTitle, IonCardSubtitle,
  IonCardContent, IonList, IonListHeader, IonItem, IonLabel,
  IonIcon, IonBadge, IonAvatar, IonButton, IonButtons,
  IonProgressBar, IonRefresher, IonRefresherContent, IonSkeletonText
} from '@ionic/vue'
import {
  ribbonOutline, pencilOutline, cafeOutline
} from 'ionicons/icons'

const userStore = useUserStore()
const profileData = useProfileData()
const i18n = useI18n()
const isLoading = ref(true)
const tierThresholds: Record<string, number> = {
  Bronze: 0,
  Silver: 1000,
  Gold: 5000,
  Platinum: 10000
}

const recentRewards = ref([
  {
    id: 1,
    name: '$10 Store Credit',
    description: 'Redeemed store credit',
    points: 1000,
    date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
    icon: ribbonOutline
  },
  {
    id: 2,
    name: 'Free Coffee',
    description: 'Redeemed beverage reward',
    points: 500,
    date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
    icon: cafeOutline
  }
])

onMounted(async () => {
  await loadData()
})

function setLoadingState(state: boolean) {
  isLoading.value = state;
}

async function loadData() {
  isLoading.value = true;
  try {
    await new Promise(resolve => setTimeout(resolve, 1000));
    await profileData.loadProfileData();
  } finally {
    isLoading.value = false;
  }
}

async function refreshData(event: CustomEvent) {
  await loadData()
  const refresher = event.target as HTMLIonRefresherElement | null
  if (refresher) {
    refresher.complete()
  }
}

function handleEdit() {
  // TODO: Implement edit profile functionality
}

function getMembershipColor() {
  const type = userStore.user?.tags?.membership_type || 'Regular'
  switch (type) {
    case 'Gold': return 'warning'
    case 'Platinum': return 'medium'
    case 'Silver': return 'light'
    default: return 'primary'
  }
}

function getLocalizedTier(tier: string): string {
  const tierKey = tier.toLowerCase();
  return i18n.t(`tiers.${tierKey}`);
}

function getCurrentTier(points: number): string {
  // First check if the user has a saved membership_type in their tags
  if (userStore.user?.tags?.membership_type) {
    return getTierDisplayName(userStore.user.tags.membership_type);
  }
  
  // Fall back to calculating from points if no membership tag exists
  let tier: keyof typeof tierThresholds;
  
  if (points >= tierThresholds.Platinum) tier = 'Platinum'
  else if (points >= tierThresholds.Gold) tier = 'Gold'
  else if (points >= tierThresholds.Silver) tier = 'Silver'
  else tier = 'Bronze'
  
  return getTierDisplayName(tier)
}

function getNextTier(points: number): string {
  // Get the current tier from the membership_type
  const currentTierName = userStore.user?.tags?.membership_type || getCurrentTier(points);
  
  // Finding the next tier based on the current saved tier
  switch (currentTierName) {
    case 'Bronze': return 'Silver';
    case 'Silver': return 'Gold';
    case 'Gold': return 'Platinum';
    default: return 'Platinum'; // If already Platinum, stay at Platinum
  }
}

function getTierDisplayName(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1)
}

function getTierProgress(points: number): number {
  const currentTierName = userStore.user?.tags?.membership_type || getCurrentTier(points);
  const nextTierName = getNextTier(points);
  
  // If already at Platinum, show 100% progress
  if (currentTierName === 'Platinum' || nextTierName === currentTierName) {
    return 1;
  }
  
  // Map the tier names to their threshold values
  const currentThresholdValue = tierThresholds[currentTierName as keyof typeof tierThresholds];
  const nextThresholdValue = tierThresholds[nextTierName as keyof typeof tierThresholds];
  
  // Calculate progress between tiers based on points
  return (points - currentThresholdValue) / (nextThresholdValue - currentThresholdValue);
}

function getPointsToNextTier(points: number): number {
  const nextTierName = getNextTier(points);
  
  // If already at highest tier, return 0
  if (nextTierName === 'Platinum' && userStore.user?.tags?.membership_type === 'Platinum') {
    return 0;
  }
  
  const nextThresholdValue = tierThresholds[nextTierName as keyof typeof tierThresholds];
  return Math.max(0, nextThresholdValue - points);
}

function formatDate(date: Date) {
  const daysDiff = Math.round((Date.now() - date.getTime()) / (24 * 60 * 60 * 1000))
  
  if (daysDiff === 0) {
    return i18n.t('dateFormat.today')
  } else if (daysDiff === 1) {
    return i18n.t('dateFormat.yesterday')
  } else {
    return i18n.t(daysDiff === 1 ? 'dateFormat.dayAgo' : 'dateFormat.daysAgo', { n: daysDiff })
  }
}

function getTierColor(tier: string): string {
  // Convert to lowercase for comparison, regardless of input format
  switch (tier) {
    case 'Platinum': return 'medium'
    case 'Gold': return 'warning'
    case 'Silver': return 'light'
    case 'Bronze': return 'tertiary'
    default: return 'primary'
  }
}

defineExpose({
  setLoadingState,
  loadData
});
</script>

<style scoped>
/* Custom CSS only for elements that can't be easily styled with Tailwind */
ion-avatar {
  --border-color: var(--ion-color-primary);
}

/* Override Tailwind classes on a component level */
:deep(.membership-badge) {
  z-index: 1;
}
</style> 